import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/useDispatch";
import {
  accountApiSlice,
  useLazyGetMeQuery,
} from "../../../api/account/accountApiSlice";
import {
  clearAccount,
  accountSessionRestored,
} from "../../../redux/slice/account/accountSlice";
import {
  clearAccountSessionToken,
  getAccountSessionToken,
} from "../../../authentication/accountSession";
import { clearBookingPersonalData } from "../../../util/clearBookingPersonalData";

/**
 * One-shot session restore, used once in the app shell (App.tsx).
 *
 * Gated on the tenant's `accountsEnabled` flag (dark shipping): when enabled
 * and a stored session token exists, fetches GET /me and hydrates the account
 * slice; a 401 clears the stale session silently. Either way the check ends
 * with `sessionChecked === true` so guards can decide.
 *
 * Init only — reactions to login/logout are handled where they happen
 * (verify page / account page), not here.
 */
export const useAccountSessionInit = (): void => {
  const dispatch = useAppDispatch();
  const configLoaded = useSelector(
    (state: RootState) => state.tenantConfig.configLoaded
  );
  const accountsEnabled = useSelector(
    (state: RootState) => state.tenantConfig.config.accountsEnabled === true
  );
  const [fetchMe] = useLazyGetMeQuery();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!configLoaded || !accountsEnabled || startedRef.current) return;
    startedRef.current = true;

    const token = getAccountSessionToken();
    if (!token) {
      dispatch(clearAccount());
      return;
    }

    // A login can complete while this is still in flight (a passkey takes
    // seconds). Its answer then belongs to a session that no longer exists:
    // restoring it would overwrite the new profile, and a 401 for it would
    // delete the new token. So an answer for a replaced token is dropped.
    const stillCurrent = () => getAccountSessionToken() === token;

    fetchMe()
      .unwrap()
      .then((profile) => {
        if (!stillCurrent()) return;
        // Not `setAccountProfile`: this is the stored session resolving, not
        // the account changing — see `accountSessionRestored` (#2793).
        dispatch(accountSessionRestored(profile));
      })
      .catch((error: unknown) => {
        if (!stillCurrent()) return;
        const status = (error as { status?: number } | null)?.status;
        if (status === 401) {
          // Stale/invalid session — drop it silently, incl. the cached
          // GET /me error entry.
          clearAccountSessionToken();
          dispatch(accountApiSlice.util.resetApiState());
          // Session expiry is a session end: forget the previous user's
          // booking-form PII (#1873 scenario 4). Idempotent with the same
          // call in axiosNewBaseQuery's /me* 401 branch. Deliberately NOT
          // done for non-401 errors (transient network failure is not a
          // session end) nor for the no-token guest path above.
          clearBookingPersonalData(dispatch);
        }
        dispatch(clearAccount());
      });
  }, [configLoaded, accountsEnabled, dispatch, fetchMe]);
};

export default useAccountSessionInit;
